import { Link } from "react-router-dom";
import { LogOut, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { toast } from "sonner";

import logo from "@/assets/habits365greek-logo.png";
import { initials as initialsOf } from "@/components/common/notes-todos/timelineDisplay";
import { SHELL_CONTENT_CLASS } from "@/components/layout/shellContent";
import { PortalSidebar } from "@/components/navigation/PortalSidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDomainOptions } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";
import { PARTNER_PORTALS } from "@/lib/auth/partnerAccess";
import { cn } from "@/lib/utils";
import { partnerDisplayName } from "@/modules/PartnerPortal/lib/session";

import { partnerNav } from "../nav";
import { usePartnerSessionContext } from "./partnerSessionContext";

/**
 * PartnerShell — chrome for the printer, FC and warehouse portals, as in the
 * prototype boards (PortalBoard F1, FCPortalBoard K1, WarehousePortalBoard WP1):
 * a narrow icon rail (the shared PortalSidebar, `railOnly`: user initials, Board,
 * Schedule; labels as tooltips), and
 * a top bar with the HABITS logo on the left and, on the right, the partner
 * chip ("🦉 Owl"), the theme toggle and the user pill. No footer.
 *
 * Below `md` the rail hides; the Board / Schedule tabs in the page head
 * navigate.
 *
 * Sign-out is the ONLY place this portal ends the session, and only on an
 * explicit click. NOTHING here calls a staff endpoint: the API refuses a
 * partner login everything but its own portal, `GET /api/me` and
 * `GET /api/platform/**`.
 */
export const PartnerShell = ({ children }: { children: React.ReactNode }) => {
  const session = usePartnerSessionContext();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { getLabel } = useDomainOptions();

  const { home } = PARTNER_PORTALS[session.partner];
  const nav = partnerNav(session.partner);
  const kindLabel = getLabel("fulfillment", "partner_kind", session.partner);
  const partnerName = partnerDisplayName(session);
  const fullName = user?.full_name?.trim() || user?.email || partnerName;
  const initials = initialsOf(fullName);

  const handleSignOut = async () => {
    await logout();
    toast.success("Signed out");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="sticky top-0 hidden h-screen md:block">
        <PortalSidebar
          className="h-screen"
          railOnly
          ariaLabel={`${kindLabel} portal`}
          groups={[{ items: nav }]}
          header={
            <span
              aria-hidden
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-xs font-bold text-primary"
            >
              {initials}
            </span>
          }
        />
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-sticky border-b border-border bg-navbar/95 text-navbar-foreground backdrop-blur-sm">
          <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-8">
            <Link to={home} className="flex items-center">
              <img src={logo} alt="Habits 365 Greek" className="h-8 object-contain dark:invert dark:brightness-200" />
            </Link>

            <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-3">
              <span className="hidden max-w-48 truncate rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-medium text-foreground sm:inline-flex">
                {partnerName}
              </span>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-card pl-3 pr-1 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Account menu"
                  >
                    <span className="hidden max-w-40 truncate sm:inline">{fullName}</span>
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {initials}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="space-y-0.5">
                    <div className="truncate text-sm font-semibold">{fullName}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">{user?.email}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">{partnerName}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className={cn("flex flex-1 flex-col overflow-auto", SHELL_CONTENT_CLASS)}>{children}</main>
      </div>
    </div>
  );
};

export default PartnerShell;
